import { useEffect } from 'react';
import IPT from 'react-immutable-proptypes';
import PT from 'prop-types';
import { Header, SubHeader, QuestionSection } from './components';
import { Container } from 'edrolo-style-guide';
import s from './styles.scss';

/*
 * This is part of the core Question Set experience.
 * Please be aware that there are two contexts to consider when working on this component:
 *    The main application experience that is rendered in `edrolo/domains/exams/components/QuestionSetPage`
 *    The demo experience that is rendered in `edrolo/domains/exams/components/QuestionSetPageDemo`
 *
 * Anything that happens in here down in the tree needs to be considered for
 * both authenticated and unauthenticated views.
 */

const QuestionSetIndex = ({
  examId,
  exam,
  classId,
  sections,
  numQuestions,
  questionsBySectionId,
  isTeacher,
  classQuestionSetAggregates,
  fetchClassQuestionSetAggregates,
  getQuestionInstructionText,
  questionResponses,
  buildQuestionSetUrl,
  schoolClassCode,
  schoolClassIsTrial,
  schoolClassExpirationDate,
  analyticsPayload,
  searchParams,
  filterToggle,
}) => {
  useEffect(() => {
    if (isTeacher) {
      fetchClassQuestionSetAggregates(classId, examId);
    }
  }, [classId, examId, isTeacher, fetchClassQuestionSetAggregates]);

  return (
    <div className={s.questionSetIndex}>
      <Header
        exam={exam}
        isTeacher={isTeacher}
        classId={classId}
        numQuestions={numQuestions}
        schoolClassCode={schoolClassCode}
        schoolClassIsTrial={schoolClassIsTrial}
        schoolClassExpirationDate={schoolClassExpirationDate}
      />

      <SubHeader
        navItems={sections}
        analyticsPayload={analyticsPayload}
        courseId={exam.get('course')}
        isTeacher={isTeacher}
        classId={classId}
        searchParams={searchParams}
        exam={exam}
        filterToggle={filterToggle}
      />

      <Container wide>
        {sections.map((section) => (
          <QuestionSection
            key={`${section.get('id')}`}
            isTeacher={isTeacher}
            exam={exam}
            classId={classId}
            section={section}
            buildQuestionSetUrl={buildQuestionSetUrl}
            questionsBySectionId={questionsBySectionId}
            classQuestionSetAggregates={classQuestionSetAggregates}
            questionResponses={questionResponses}
            getQuestionInstructionText={getQuestionInstructionText}
            searchParams={searchParams}
          />
        ))}
      </Container>
    </div>
  );
};

QuestionSetIndex.propTypes = {
  exam: IPT.map.isRequired,
  examId: PT.string,
  classId: PT.oneOfType([PT.string, PT.number]).isRequired,
  numQuestions: PT.number,
  sections: IPT.list,
  questionsBySectionId: IPT.map,
  questionResponses: PT.any,
  isTeacher: PT.bool,
  classQuestionSetAggregates: PT.any,
  fetchClassQuestionSetAggregates: PT.func,
  buildQuestionSetUrl: PT.func,
  getQuestionInstructionText: PT.func,
  schoolClassCode: PT.string,
  schoolClassIsTrial: PT.bool,
  schoolClassExpirationDate: PT.string,
  analyticsPayload: PT.object,
  searchParams: PT.object.isRequired,
  filterToggle: PT.shape({
    hasSpiciness: PT.bool.isRequired,
    hasWorkingPaths: PT.bool.isRequired,
  }).isRequired,
};

export default QuestionSetIndex;
